import { useQuery, useQueryClient, type UseQueryResult } from 'react-query';
import { useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../services/ApiClient';
import { type TideInfo, type TideDataViewModel } from '../../components/Map/types';
import { type Timestamp } from '../../services/Timestamp';
import { useMapContext } from '../../components/GlobalContextProvider/GlobalContextProvider';
import { ApiEndpoints } from '../../constants/api-constants';
import { Queries, QueryCacheConfig } from '../../constants/query-constants';
import { isTimestampWithinRange } from '../../utils/timestamp-range-utils';

export const useTidalData = (operation: string, timestamp: Timestamp): UseQueryResult<TideDataViewModel> => {
  const { settings } = useMapContext();
  const client = useQueryClient();
  const [tideInfo, setTideInfo] = useState<TideInfo[]>([]);
  const enabled = false;

  const getTideInfo = async (): Promise<TideInfo> => {
    return apiClient.get<TideInfo>(ApiEndpoints.tides.getTideInfo(operation, timestamp));
  };

  const { data: tideInfoResponse } = useQuery({
    ...QueryCacheConfig[Queries.TideInfo],
    queryKey: Queries.TideInfo,
    queryFn: getTideInfo,
    enabled: enabled && operation && settings?.showQuiverPlot,
  });

  useEffect(() => {
    if (tideInfoResponse?.isDataAvailable && !tideInfo.some(x => x.id === tideInfoResponse.id)) {
      setTideInfo([...tideInfo, tideInfoResponse]);
    }
  }, [tideInfoResponse]);

  const currentTideInfo = useMemo(() => {
    return tideInfo.find(info => isTimestampWithinRange(timestamp, info.range));
  }, [timestamp, tideInfo]);

  useEffect(() => {
    if (tideInfoResponse?.isDataAvailable && !currentTideInfo) {
      client.removeQueries(Queries.TideInfo);
    }
  }, [currentTideInfo]);

  const getTidalData = async (): Promise<TideDataViewModel> => {
    return apiClient.get<TideDataViewModel>(ApiEndpoints.tides.getTides(tideInfoResponse.id));
  };

  return useQuery<TideDataViewModel, Error>({
    ...QueryCacheConfig[Queries.TidalData],
    queryKey: [Queries.TidalData, currentTideInfo?.id],
    queryFn: getTidalData,
    enabled: !!currentTideInfo,
  });
};
